/*
 * <scrolling-promotion-bar> fills its viewport with inert copies of one source
 * sequence, then moves exactly one sequence width with a compositor animation.
 * No per-frame JavaScript is used.
 */
if (!customElements.get('scrolling-promotion-bar')) {
  customElements.define(
    'scrolling-promotion-bar',
    class ScrollingPromotionBar extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('.sprom__track');
        this.sequence = this.querySelector('.sprom__sequence');
        if (!this.track || !this.sequence) return;

        this.speed = Math.max(parseFloat(this.dataset.speed) || 40, 1);
        this.direction = this.dataset.direction === 'right' ? 'right' : 'left';
        this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.abortController = new AbortController();
        this.isIntersecting = true;
        this.isPointerPaused = false;
        this.isFocusPaused = false;
        this.scheduleBuild = this.scheduleBuild.bind(this);

        this.resizeObserver = new ResizeObserver(this.scheduleBuild);
        this.resizeObserver.observe(this);
        this.resizeObserver.observe(this.sequence);

        this.intersectionObserver = new IntersectionObserver((entries) => {
          this.isIntersecting = entries[0]?.isIntersecting ?? true;
          this.syncPlayback();
        });
        this.intersectionObserver.observe(this);

        this.motionQuery.addEventListener('change', this.scheduleBuild, {
          signal: this.abortController.signal,
        });

        if (this.hasAttribute('data-pause-on-hover')) this.bindPauseEvents();

        this.querySelectorAll('img').forEach((image) => {
          if (!image.complete) {
            image.addEventListener('load', this.scheduleBuild, {
              once: true,
              signal: this.abortController.signal,
            });
          }
        });

        this.build();
      }

      disconnectedCallback() {
        this.abortController?.abort();
        this.resizeObserver?.disconnect();
        this.intersectionObserver?.disconnect();
        this.animation?.cancel();
        if (this.buildFrame) cancelAnimationFrame(this.buildFrame);
      }

      bindPauseEvents() {
        const options = { signal: this.abortController.signal };

        this.addEventListener(
          'pointerenter',
          () => {
            this.isPointerPaused = true;
            this.syncPlayback();
          },
          options
        );
        this.addEventListener(
          'pointerleave',
          () => {
            this.isPointerPaused = false;
            this.syncPlayback();
          },
          options
        );
        this.addEventListener(
          'focusin',
          () => {
            this.isFocusPaused = true;
            this.syncPlayback();
          },
          options
        );
        this.addEventListener(
          'focusout',
          (event) => {
            if (event.relatedTarget && this.contains(event.relatedTarget)) return;
            this.isFocusPaused = false;
            this.syncPlayback();
          },
          options
        );
      }

      scheduleBuild() {
        if (this.buildFrame) cancelAnimationFrame(this.buildFrame);
        this.buildFrame = requestAnimationFrame(() => {
          this.buildFrame = null;
          this.build();
        });
      }

      build() {
        this.animation?.cancel();
        this.animation = null;
        this.track.querySelectorAll('[data-sprom-clone]').forEach((clone) => clone.remove());

        const sequenceWidth = this.sequence.getBoundingClientRect().width;
        if (!sequenceWidth || this.motionQuery.matches) return;

        const cloneCount = Math.ceil(this.clientWidth / sequenceWidth) + 1;
        const fragment = document.createDocumentFragment();

        for (let index = 0; index < cloneCount; index += 1) {
          const clone = this.sequence.cloneNode(true);
          clone.setAttribute('data-sprom-clone', '');
          clone.setAttribute('aria-hidden', 'true');
          clone.setAttribute('inert', '');
          clone.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
          fragment.appendChild(clone);
        }

        this.track.appendChild(fragment);

        const keyframes =
          this.direction === 'right'
            ? [
                { transform: `translateX(-${sequenceWidth}px)` },
                { transform: 'translateX(0)' },
              ]
            : [
                { transform: 'translateX(0)' },
                { transform: `translateX(-${sequenceWidth}px)` },
              ];

        this.animation = this.track.animate(keyframes, {
          duration: (sequenceWidth / this.speed) * 1000,
          iterations: Infinity,
          easing: 'linear',
        });
        this.syncPlayback();
      }

      syncPlayback() {
        if (!this.animation) return;
        const shouldPause = !this.isIntersecting || this.isPointerPaused || this.isFocusPaused;
        if (shouldPause) this.animation.pause();
        else this.animation.play();
      }
    }
  );
}
